import React from 'react';
import { HiCalendar, HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import { useSelector, useDispatch } from "react-redux";
import { CompactSelect } from '../../../../components/common/Search';
import { updateCapacityPlanningFilters } from '../../../../redux/slices/resources';
import { useJwt } from '../../../../helpers/common';
import PositionedTooltip from '../../../../components/common/PositionedTooltip';
import { useUrlFilters } from '../../../../hooks/useUrlFilters';

const SPRINT_RANGE_OPTIONS = [
  { value: 'current', label: 'Current Sprint Only' },
  { value: 'past-current', label: 'Past & Current Sprints' },
  { value: 'current-future', label: 'Current & Future Sprints' },
  { value: 'all', label: 'Past, Current & Future Sprints' }
];

interface TimelineHeaderProps {
  searchBarComponent?: React.ReactNode;
  onPrevClick: () => void;
  onTodayClick: () => void;
  onNextClick: () => void;
  searchType?: string; // To determine if in sprint filter mode
  isPrevDisabled?: boolean; // Disable previous button when no previous sprint
  isNextDisabled?: boolean; // Disable next button when no next sprint
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

const TimelineHeader: React.FC<TimelineHeaderProps> = ({
  searchBarComponent,
  onPrevClick,
  onTodayClick,
  onNextClick,
  searchType = '',
  isPrevDisabled = false,
  isNextDisabled = false,
  hasActiveFilters = false,
  onClearFilters,
}) => {
  const dispatch = useDispatch();
  const JWTdata: any = useJwt();
  const org_id = JWTdata?.org_id;

  const {
    teamFilterState,
    capacityPlanningFilters
  } = useSelector((state: any) => state.resources);
  
  const { setFilter } = useUrlFilters([
    { urlKey: 'sprintRange', storageKey: 'res_sprintRange', defaultValue: 'current' }
  ]);
  
  const sprintRangeSelection = capacityPlanningFilters?.sprintRangeSelection || 'current';
  const selectedTeams = capacityPlanningFilters?.selectedTeams || [];

  // Determine if we should show sprint-specific labels
  const isTeamFilterSprintMode = teamFilterState.sprint_mode &&
    teamFilterState.team_uuid &&
    teamFilterState.sorted_sprints &&
    teamFilterState.sorted_sprints.length > 0;

  const isSprintFilterMode = searchType === 'sprint';
  const isSprintMode = isTeamFilterSprintMode || isSprintFilterMode;

  // Hide navigation buttons in sprint filter mode (replaced by sprint range selector)
  const shouldShowNavigationButtons = searchType !== 'sprint';

  const isSprintRangeDisabled = !selectedTeams || selectedTeams.length !== 1;

  const prevButtonText = isSprintMode ? 'Previous Sprint' : 'Prev';
  const todayButtonText = isSprintMode ? 'Current Sprint' : 'Today';
  const nextButtonText = isSprintMode ? 'Next Sprint' : 'Next';

  return (
    <div className="h-[75px] flex items-center justify-between w-full py-3 px-4 bg-light-surfacePrimary dark:bg-secondary-dark border-b border-light-border-default dark:border-gray-700">
      {/* Left side - Search component */}
      <div className="flex-1 flex flex-row items-center">
        {searchBarComponent && (
          <div className="mr-4">
            {searchBarComponent}
          </div>
        )}
      </div>

      {/* Center - Clear Filters link */}
      {hasActiveFilters && onClearFilters && (
        <button
          onClick={onClearFilters}
          className="text-sm text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium whitespace-nowrap transition-colors cursor-pointer hover:underline mx-3"
        >
          Clear Filters
        </button>
      )}

      {/* Right side - Navigation buttons or Sprint Range Selector */}
      {shouldShowNavigationButtons ? (
        <div className="flex items-center gap-3">
          <div className="relative group">
            <button
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium
                         ${isPrevDisabled
                  ? 'text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:border-gray-400 dark:hover:border-gray-500'}
                         rounded-lg transition-colors duration-200
                         focus:outline-none focus:ring-1 focus:ring-gray-300/30 dark:focus:ring-gray-400/30`}
              onClick={onPrevClick}
              disabled={isPrevDisabled}
            >
              <HiChevronLeft className="w-4 h-4" />
              {prevButtonText}
            </button>
            {isPrevDisabled && (
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999]">
                No previous sprint available
                <div className="absolute right-full top-1/2 transform -translate-y-1/2 -mr-1 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
              </div>
            )}
          </div>
          <button
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                       bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600
                       hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white
                       hover:border-gray-400 dark:hover:border-gray-500
                       rounded-lg transition-colors duration-200
                       focus:outline-none focus:ring-1 focus:ring-gray-300/30 dark:focus:ring-gray-400/30"
            onClick={onTodayClick}
          >
            <HiCalendar className="w-4 h-4" />
            {todayButtonText}
          </button>
          <div className="relative group">
            <button
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium
                         ${isNextDisabled
                  ? 'text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:border-gray-400 dark:hover:border-gray-500'}
                         rounded-lg transition-colors duration-200
                         focus:outline-none focus:ring-1 focus:ring-gray-300/30 dark:focus:ring-gray-400/30`}
              onClick={onNextClick}
              disabled={isNextDisabled}
            >
              {nextButtonText}
              <HiChevronRight className="w-4 h-4" />
            </button>
            {isNextDisabled && (
              <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999]">
                No next sprint available
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 -ml-1 border-4 border-transparent border-l-gray-900 dark:border-l-gray-700"></div>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Sprint Range Selector (when sprint filter is active)
        <PositionedTooltip
          disabled={!isSprintRangeDisabled}
          placement="bottom"
          width={320}
          content="Sprint range view is only available when a single team is selected. Please select one team to view sprint ranges."
        >
          <CompactSelect
            options={SPRINT_RANGE_OPTIONS}
            placeholder="Select sprint range"
            value={sprintRangeSelection}
            width="min-w-[250px]"
            onChange={(value) => {
              setFilter('sprintRange', value || 'current');
            }}
            disabled={isSprintRangeDisabled}
          />
        </PositionedTooltip>
      )}
    </div>
  );
};

export default TimelineHeader;